import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Loader2, Copy, Clock, Target, TrendingUp, TrendingDown, Activity, Twitter, Send, RefreshCw, X, Calendar, ArrowUpRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { formatUsdCompact } from "@/lib/formatUsd";
import { useSocket } from "@/context/SocketContext";
import { useApp } from "@/context/AppContext";
import { TokenAvatar } from "@/components/TokenAvatar";
import { tokenMetaCache } from "@/lib/tokenMetaCache";
import { fetchTokenMeta } from "@/lib/tokenMeta";
import { TraderCalendarModal } from "@/components/TraderCalendarModal";
import { prefetchCandles, prefetchBatchCandles } from "@/lib/pumpCandles";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(ms: number) {
  if (!ms || ms < 0) return "0s";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remainingSecs}s`;
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (hrs < 24) return `${hrs}h ${remainingMins}m`;
  const days = Math.floor(hrs / 24);
  const remainingHrs = hrs % 24;
  return `${days}d ${remainingHrs}h`;
}

function truncate(str: string, max = 8) {
  if (!str) return "";
  if (str.length <= max) return str;
  return str.substring(0, 4) + "..." + str.substring(str.length - 4);
}

type TimeframeScope = "1h" | "2h" | "6h" | "12h" | "1d" | "2d" | "1w" | "2w" | "1m" | "2m" | "All";

const TIMEFRAME_CONFIG: { id: TimeframeScope; label: string; ms: number }[] = [
  { id: "1h", label: "1h", ms: 1 * 3600 * 1000 },
  { id: "2h", label: "2h", ms: 2 * 3600 * 1000 },
  { id: "6h", label: "6h", ms: 6 * 3600 * 1000 },
  { id: "12h", label: "12h", ms: 12 * 3600 * 1000 },
  { id: "1d", label: "1d", ms: 24 * 3600 * 1000 },
  { id: "2d", label: "2d", ms: 2 * 24 * 3600 * 1000 },
  { id: "1w", label: "1w", ms: 7 * 24 * 3600 * 1000 },
  { id: "2w", label: "2w", ms: 14 * 24 * 3600 * 1000 },
  { id: "1m", label: "1m", ms: 30 * 24 * 3600 * 1000 },
  { id: "2m", label: "2m", ms: 60 * 24 * 3600 * 1000 },
  { id: "All", label: "All", ms: Infinity },
];

function formatPct(value: number, decimals = 2, showPlus = true): string {
  if (isNaN(value) || !isFinite(value)) return "0.00%";
  const sign = showPlus && value > 0 ? "+" : value < 0 ? "-" : "";
  const absVal = Math.abs(value);
  const formatted = absVal.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${sign}${formatted}%`;
}

function PnlCard({ label, pct, sol, theme, children }: { label: string; pct: number; sol: number; theme: "emerald" | "blue" | "purple"; children?: React.ReactNode }) {
  const isPositive = pct >= 0;
  
  const textColor = isPositive 
    ? (theme === "emerald" ? "text-emerald-400" : theme === "blue" ? "text-blue-400" : "text-purple-400")
    : "text-rose-400";
    
  const badgeColors = isPositive
    ? (theme === "emerald" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.15)]" : 
       theme === "blue" ? "bg-blue-500/10 text-blue-400 border-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.15)]" : 
       "bg-purple-500/10 text-purple-400 border-purple-500/20 shadow-[0_0_10px_rgba(168,85,247,0.15)]")
    : "bg-rose-500/10 text-rose-400 border-rose-500/20 shadow-[0_0_10px_rgba(244,63,94,0.15)]";
    
  const labelColor = theme === "emerald" ? "text-emerald-400/50" : theme === "blue" ? "text-blue-400/50" : "text-purple-400/50";
  const borderColor = theme === "emerald" ? "border-emerald-500/10" : theme === "blue" ? "border-blue-500/10" : "border-purple-500/10";

  return (
    <div className={`bg-neutral-900 border ${borderColor} rounded-xl p-3 flex flex-col gap-1 relative overflow-hidden transition-all duration-300 hover:border-white/10 hover:bg-neutral-900/80`}>
      <span className={`text-[9px] uppercase tracking-wider ${labelColor} font-bold`}>{label}</span>
      <div className="flex flex-col gap-2 mt-0.5 items-start">
        <span className={`text-xl sm:text-2xl font-mono font-black tracking-tight ${textColor}`}>
          {formatPct(pct, 2, true)}
        </span>
        <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-bold border ${badgeColors} animate-in fade-in slide-in-from-bottom-2 duration-700`}>
          {sol >= 0 ? '+' : ''}{sol.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SOL
        </div>
      </div>
      {children}
    </div>
  );
}

function handleCopy(text: string) {
  if (text) navigator.clipboard.writeText(text);
}

// ─── Animation CSS (injected once into the document head) ─────────────────────

const ANIM_CSS = `
@keyframes sbt-ticker-up {
  0%   { opacity: 1; transform: translateY(0) scale(1); }
  70%  { opacity: 0.9; }
  100% { opacity: 0; transform: translateY(-20px) scale(0.82); }
}
@keyframes sbt-ticker-down {
  0%   { opacity: 1; transform: translateY(0) scale(1); }
  70%  { opacity: 0.9; }
  100% { opacity: 0; transform: translateY(20px) scale(0.82); }
}
@keyframes sbt-row-flash-green {
  0%   { background: rgba(16,185,129,0.14); box-shadow: inset 3px 0 0 #10b981, 0 0 28px rgba(16,185,129,0.28); }
  55%  { background: rgba(16,185,129,0.05); box-shadow: inset 1px 0 0 rgba(16,185,129,0.4); }
  100% { background: transparent; box-shadow: none; }
}
@keyframes sbt-row-flash-red {
  0%   { background: rgba(244,63,94,0.13); box-shadow: inset 3px 0 0 #f43f5e, 0 0 24px rgba(244,63,94,0.24); }
  55%  { background: rgba(244,63,94,0.04); box-shadow: inset 1px 0 0 rgba(244,63,94,0.3); }
  100% { background: transparent; box-shadow: none; }
}
@keyframes sbt-row-flash-pnl {
  0%   { background: rgba(16,185,129,0.07); }
  100% { background: transparent; }
}
@keyframes sbt-rank-badge {
  0%   { opacity: 0; transform: scale(0.3) translateY(3px); }
  18%  { opacity: 1; transform: scale(1.2) translateY(0); }
  38%  { transform: scale(1); }
  72%  { opacity: 1; }
  100% { opacity: 0; transform: scale(0.85) translateY(-2px); }
}
@keyframes sbt-float-delta {
  0%   { opacity: 1; transform: translateY(0) scale(1); }
  65%  { opacity: 0.85; }
  100% { opacity: 0; transform: translateY(-32px) scale(0.8); }
}
@keyframes sbt-runner-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.45; }
}
@keyframes sbt-catalyst-flash {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
}
@keyframes sbt-ambient-shimmer {
  0%, 100% { background-color: transparent; }
  50%       { background-color: rgba(16, 185, 129, 0.02); }
}
`;

let animStyleInjected = false;
function ensureAnimStyles() {
  if (animStyleInjected) return;
  const el = document.createElement("style");
  el.textContent = ANIM_CSS;
  document.head.appendChild(el);
  animStyleInjected = true;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function RunnerScoreBadge({ score }: { score: number | null | undefined }) {
  if (!score || score <= 0) return null;
  if (score >= 80) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[8px] font-black font-mono px-1 py-0.5 rounded border text-emerald-400 border-emerald-500/50 bg-emerald-500/10"
        style={{ animation: "sbt-runner-pulse 1.8s ease-in-out infinite" }}
        title={`Runner score: ${score}`}
      >
        ▲{score}
      </span>
    );
  }
  if (score >= 50) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[8px] font-bold font-mono px-1 py-0.5 rounded border text-amber-400 border-amber-500/30 bg-amber-500/8"
        title={`Runner score: ${score}`}
      >
        ◆{score}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[8px] font-mono px-1 py-0.5 rounded border text-white/25 border-white/10 bg-white/4"
      title={`Runner score: ${score}`}
    >
      ·{score}
    </span>
  );
}

interface PnlDeltaTickerProps { delta: number; delayMs?: number; }
function PnlDeltaTicker({ delta, delayMs = 0 }: PnlDeltaTickerProps) {
  const isUp = delta > 0;
  const pct = Math.abs(delta * 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <span
      className={`absolute right-0 -top-4 text-[9px] font-black font-mono pointer-events-none whitespace-nowrap ${isUp ? "text-emerald-300" : "text-rose-300"}`}
      style={{ animation: `${isUp ? "sbt-ticker-up" : "sbt-ticker-down"} 2.5s ease-out ${delayMs}ms forwards`, zIndex: 20 }}
    >
      {isUp ? "+" : "−"}{pct}%
    </span>
  );
}

interface RankMoveBadgeProps { move: number; flashTs: number; delayMs?: number; }
function RankMoveBadge({ move, flashTs, delayMs = 0 }: RankMoveBadgeProps) {
  const isUp = move > 0;
  const abs = Math.abs(move);
  // flashTs used as key by caller to restart animation
  void flashTs;
  return (
    <span
      className={`text-[9px] font-black font-mono pointer-events-none leading-none ${isUp ? "text-emerald-400" : "text-rose-400"}`}
      style={{ animation: `sbt-rank-badge 2.8s ease-out ${delayMs}ms forwards` }}
    >
      {isUp ? "▲" : "▼"}{abs}
    </span>
  );
}

// ─── Animation state types ────────────────────────────────────────────────────

interface PositionAnim {
  pnlDelta: number | null;   // fractional (0.05 = +5pp)
  rankMove: number | null;   // positive = climbed, negative = fell
  flashTs: number;           // used as React key so animation restarts on update
  delayMs?: number;          // staggered delay to distribute activity
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ServerBotTrades() {
  const [trades, setTrades] = useState<any[]>([]);
  const [openPositions, setOpenPositions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [lastPollTime, setLastPollTime] = useState(Date.now());
  const [lastWsEvent, setLastWsEvent] = useState<number | null>(null);
  const { serverOpenPositions, socket, serverPaperStats } = useSocket();
  const [totalServerTrades, setTotalServerTrades] = useState<number>(0);
  const [hasMoreTrades, setHasMoreTrades] = useState<boolean>(false);
  const [isStreamingTrades, setIsStreamingTrades] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const tradesContainerRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { setCaMintInput, solPrice = 150, addAppToast } = useApp() as any;

  // Inject CSS keyframes once
  useEffect(() => { ensureAnimStyles(); }, []);

  // Background prefetch candles for open positions & recent trades to ensure 0ms instant chart loading
  useEffect(() => {
    const mints: string[] = [];
    if (serverOpenPositions && Array.isArray(serverOpenPositions)) {
      for (const p of serverOpenPositions) {
        if (p.mint && !mints.includes(p.mint)) mints.push(p.mint);
      }
    }
    if (trades && Array.isArray(trades)) {
      for (const t of trades.slice(0, 8)) {
        if (t.mint && !mints.includes(t.mint)) mints.push(t.mint);
      }
    }
    if (mints.length > 0) {
      prefetchBatchCandles(mints, "1s", 8);
    }
  }, [serverOpenPositions, trades]);

  const handleCloseAll = async () => {
    if (!window.confirm("Close ALL open positions at market price?")) return;
    try {
      await fetch('/api/bot-close-all', { method: 'POST' });
    } catch(e) {
      console.error("Failed to close all:", e);
    }
  };

  // — Animation tracking state —
  const [animState, setAnimState] = useState<Record<string, PositionAnim>>({});
  const [floatingDelta, setFloatingDelta] = useState<{ value: number; ts: number; symbol?: string } | null>(null);
  const prevPnlRef = useRef<Record<string, number>>({});
  const prevRankRef = useRef<Record<string, number>>({});
  const animCleanup = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const floatingCleanup = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [timeframe, setTimeframe] = useState<TimeframeScope>("All");
  const [localServerStats, setLocalServerStats] = useState<any | null>(null);

  // Continuously sync backend scoped stats for the active timeframe
  useEffect(() => {
    let cancelled = false;
    const fetchStats = () => {
      fetch(`/api/server-paper-stats?timeframe=${timeframe}`)
        .then(res => res.json())
        .then(data => {
          if (!cancelled && data && !data.error) {
            setLocalServerStats(data);
          }
        })
        .catch(() => {});
    };

    fetchStats();
    const interval = window.setInterval(fetchStats, 6000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [timeframe]);

  // Tick "now" every second for time-in-trade display
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Initial REST fetch in case WebSocket initial payload is delayed
  useEffect(() => {
    fetch("/api/server-paper-trades?limit=50")
      .then(res => res.json())
      .then(data => {
        if (data && Array.isArray(data.trades)) {
          setTrades(prev => {
            if (prev.length > 0) return prev;
            return data.trades;
          });
          if (data.totalCount) setTotalServerTrades(data.totalCount);
          setHasMoreTrades(Boolean(data.hasMore));
        }
      })
      .catch(() => {});
  }, []);

  // React to socket position updates
  useEffect(() => {
    if (serverOpenPositions) {
      if (serverOpenPositions.length === 0) {
        setOpenPositions([]);
      } else {
        setOpenPositions(prev => {
          const metaMap = new Map();
          prev.forEach(p => {
            if (p.tokenMeta && Object.keys(p.tokenMeta).length > 0) metaMap.set(p.mint, p.tokenMeta);
          });
          return serverOpenPositions.map((d: any) => {
            const cached = d.mint ? tokenMetaCache.get(d.mint) : undefined;
            const existing = (d.tokenMeta && Object.keys(d.tokenMeta).length > 0) ? d.tokenMeta : metaMap.get(d.mint) || {};
            const resolved = {
              ...cached,
              ...existing,
              name: existing.name || cached?.name,
              symbol: existing.symbol || cached?.symbol,
              imageUri: existing.imageUri || existing.image || cached?.imageUri,
              image: existing.imageUri || existing.image || cached?.imageUri,
            };

            // If missing image or symbol, trigger client-side fetch in background
            if ((!resolved.imageUri || !resolved.symbol) && d.mint) {
              fetchTokenMeta(d.mint).then(meta => {
                if (meta) {
                  setOpenPositions(curr => curr.map(item => {
                    if (item.mint === d.mint) {
                      return {
                        ...item,
                        tokenMeta: {
                          ...item.tokenMeta,
                          ...meta,
                          imageUri: meta.imageUri || item.tokenMeta?.imageUri,
                        }
                      };
                    }
                    return item;
                  }));
                }
              }).catch(() => {});
            }

            return {
              ...d,
              tokenMeta: resolved
            };
          });
        });
      }
      setNow(Date.now());
    }
  }, [serverOpenPositions]);

  // Track raw socket events & listen for new closed trades
  useEffect(() => {
    if (!socket) return;
    const handler = () => setLastWsEvent(Date.now());
    
    const enrichTrade = (t: any) => {
      const cached = t.mint ? tokenMetaCache.get(t.mint) : undefined;
      const existing = t.tokenMeta || {};
      const resolved = {
        ...cached,
        ...existing,
        name: existing.name || cached?.name,
        symbol: existing.symbol || cached?.symbol,
        imageUri: existing.imageUri || existing.image || cached?.imageUri,
      };
      if ((!resolved.imageUri || !resolved.symbol) && t.mint) {
        fetchTokenMeta(t.mint).catch(() => {});
      }
      return { ...t, tokenMeta: resolved };
    };

    const newTradeHandler = (newTrade: any) => {
      setTrades(prev => [enrichTrade(newTrade), ...prev].sort((a, b) => b.timestamp - a.timestamp));
    };

    const historicalTradesHandler = (historicalTrades: any) => {
      if (Array.isArray(historicalTrades)) {
        setTrades(historicalTrades.map(enrichTrade));
        setTotalServerTrades(historicalTrades.length);
        setLoading(false);
      } else if (historicalTrades && Array.isArray(historicalTrades.trades)) {
        setTrades(historicalTrades.trades.map(enrichTrade));
        setTotalServerTrades(historicalTrades.totalCount || historicalTrades.trades.length);
        setHasMoreTrades(Boolean(historicalTrades.hasMore));
        setIsStreamingTrades(Boolean(historicalTrades.hasMore));
        setLoading(false);
      }
    };

    const streamChunkHandler = (chunkPayload: any) => {
      if (chunkPayload && Array.isArray(chunkPayload.trades)) {
        setTrades(prev => {
          const existingIds = new Set(prev.map(t => t.id || `${t.mint}_${t.timestamp}`));
          const incoming = chunkPayload.trades
            .filter((t: any) => !existingIds.has(t.id || `${t.mint}_${t.timestamp}`))
            .map(enrichTrade);
          if (incoming.length === 0) return prev;
          return [...prev, ...incoming].sort((a, b) => b.timestamp - a.timestamp);
        });
        if (chunkPayload.totalCount) setTotalServerTrades(chunkPayload.totalCount);
        setHasMoreTrades(Boolean(chunkPayload.hasMore));
        setIsStreamingTrades(!chunkPayload.isLast && Boolean(chunkPayload.hasMore));
      }
    };

    const streamCompleteHandler = (data: any) => {
      if (data?.totalCount) setTotalServerTrades(data.totalCount);
      setIsStreamingTrades(false);
      setHasMoreTrades(false);
    };

    socket.on('server-paper-open-positions', handler);
    socket.on('server-paper-new-trade', newTradeHandler);
    socket.on('server-paper-historical-trades', historicalTradesHandler);
    socket.on('server-paper-trades-stream-chunk', streamChunkHandler);
    socket.on('server-paper-trades-stream-complete', streamCompleteHandler);
    
    return () => {
      socket.off('server-paper-open-positions', handler);
      socket.off('server-paper-new-trade', newTradeHandler);
      socket.off('server-paper-historical-trades', historicalTradesHandler);
      socket.off('server-paper-trades-stream-chunk', streamChunkHandler);
      socket.off('server-paper-trades-stream-complete', streamCompleteHandler);
    };
  }, [socket]);

  const loadMoreTrades = useCallback(() => {
    if (isLoadingMore || (!hasMoreTrades && totalServerTrades > 0 && trades.length >= totalServerTrades)) return;
    setIsLoadingMore(true);

    if (socket && socket.connected) {
      socket.emit('load-more-server-paper-trades', { offset: trades.length, limit: 50 });
      setTimeout(() => setIsLoadingMore(false), 500);
    } else {
      fetch(`/api/server-paper-trades?offset=${trades.length}&limit=50`)
        .then(res => res.json())
        .then(data => {
          if (data && Array.isArray(data.trades)) {
            setTrades(prev => {
              const existingIds = new Set(prev.map(t => t.id || `${t.mint}_${t.timestamp}`));
              const incoming = data.trades.filter((t: any) => !existingIds.has(t.id || `${t.mint}_${t.timestamp}`));
              return [...prev, ...incoming].sort((a, b) => b.timestamp - a.timestamp);
            });
            if (data.totalCount) setTotalServerTrades(data.totalCount);
            setHasMoreTrades(Boolean(data.hasMore));
          }
        })
        .catch(err => console.error("Error loading more trades:", err))
        .finally(() => setIsLoadingMore(false));
    }
  }, [isLoadingMore, hasMoreTrades, totalServerTrades, trades.length, socket]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 250) {
      loadMoreTrades();
    }
  };

  // Sorted positions (best PnL first)
  const sortedOpenPositions = [...openPositions].sort((a, b) => (b.pnlPct || 0) - (a.pnlPct || 0));

  // Detect PnL deltas and rank changes → trigger per-row animations
  useEffect(() => {
    if (sortedOpenPositions.length === 0) return;
    const ts = Date.now();
    const updates: Record<string, PositionAnim> = {};
    let maxUsdDelta = 0;
    let deltaSymbol = "";
    let totalUsdDelta = 0;

    sortedOpenPositions.forEach((pos, index) => {
      const { mint } = pos;
      const pnlPct = pos.pnlPct ?? 0;
      const prevPnl = prevPnlRef.current[mint];
      const prevRank = prevRankRef.current[mint];

      // Only flag meaningful PnL changes (> 0.05pp to avoid noise)
      const pnlDelta = prevPnl !== undefined && Math.abs(pnlPct - prevPnl) > 0.0005
        ? pnlPct - prevPnl : null;
      // Rank: lower index = better (higher PnL)
      const rankMove = prevRank !== undefined && prevRank !== index
        ? prevRank - index : null; // positive = climbed

      if (pnlDelta !== null || rankMove !== null) {
        // Distribute animation start times across rows so they don't all flash simultaneously
        const staggerDelay = (index * 220) % 1500;
        updates[mint] = { pnlDelta, rankMove, flashTs: ts, delayMs: staggerDelay };

        if (pnlDelta !== null) {
          const usdDelta = pnlDelta * (pos.sizeUsd || 0);
          totalUsdDelta += usdDelta;
          if (Math.abs(usdDelta) > Math.abs(maxUsdDelta)) {
            maxUsdDelta = usdDelta;
            const meta = pos.tokenMeta || {};
            deltaSymbol = meta.symbol || truncate(mint, 4);
          }
        }

        // Auto-clear after 2.7s + staggerDelay
        if (animCleanup.current[mint]) clearTimeout(animCleanup.current[mint]);
        animCleanup.current[mint] = setTimeout(() => {
          setAnimState(prev => {
            const next = { ...prev };
            delete next[mint];
            return next;
          });
        }, 2700 + staggerDelay);
      }

      // Persist for next comparison
      prevPnlRef.current[mint] = pnlPct;
      prevRankRef.current[mint] = index;
    });

    if (Object.keys(updates).length > 0) {
      setAnimState(prev => ({ ...prev, ...updates }));
    }

    if (Math.abs(totalUsdDelta) > 0.005) {
      setFloatingDelta({ value: totalUsdDelta, ts, symbol: deltaSymbol });
      if (floatingCleanup.current) clearTimeout(floatingCleanup.current);
      floatingCleanup.current = setTimeout(() => setFloatingDelta(null), 2300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPositions]); // watch raw openPositions, sortedOpenPositions is derived


  // — Data fetch —

  const handleCopyPositions = () => {
    let text = "";
    if (openPositions && openPositions.length > 0) {
      text += "Mint | Name | Entry MC | Live MC | Holders | PnL | Catalyst | Last Active | Time in Trade\n";
      text += "---|---|---|---|---|---|---|---|---\n";
      openPositions.forEach(p => {
        const meta = p.tokenMeta || {};
        const name = meta.name ? meta.name.replace(/\|/g, '') : truncate(p.mint, 4);
        const entryMc = p.entryMcUsd ? formatUsdCompact(p.entryMcUsd) : '-';
        const liveMc = p.currentMcUsd ? formatUsdCompact(p.currentMcUsd) : '-';
        const holders = p.holderCount ? p.holderCount.toLocaleString() : 'N/A';
        const pnlPct = p.pnlPct != null ? p.pnlPct : 0;
        const pnlPctStr = p.pnlPct != null ? `${(pnlPct > 0 ? '+' : '')}${(pnlPct * 100).toFixed(2)}%` : '-';
        const pnlSol = ((p.sizeUsd || 0) * pnlPct) / solPrice;
        const pnlSolStr = `${pnlSol >= 0 ? '+' : ''}${pnlSol.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SOL`;
        const pnl = `${pnlPctStr} (${pnlSolStr})`;
        
        const catalyst = p.catalystSol ? `${p.catalystIsBuy ? 'BUY' : 'SELL'} ${p.catalystSol.toFixed(2)} SOL` : 'None';
        const active = p.lastActivity ? formatDuration(Date.now() - p.lastActivity) + ' ago' : '-';
        const timeInTrade = p.timestamp ? formatDuration(Date.now() - p.timestamp) : '-';
        text += `${p.mint} | ${name} | ${entryMc} | ${liveMc} | ${holders} | ${pnl} | ${catalyst} | ${active} | ${timeInTrade}\n`;
      });
    }

    if (closedTrades && closedTrades.length > 0) {
      if (text) text += "\n\n";
      text += "--- Trade History (Closed) ---\n";
      text += "Mint | Name | Entry MC | Exit MC | PnL | Duration | Alpha / Reason\n";
      text += "---|---|---|---|---|---|---\n";
      closedTrades.forEach(t => {
        const meta = t.tokenMeta || {};
        const name = meta.name ? meta.name.replace(/\|/g, '') : truncate(t.mint, 4);
        const entryMc = t.entryMcUsd ? formatUsdCompact(t.entryMcUsd) : '-';
        const exitMc = t.exitMcUsd ? formatUsdCompact(t.exitMcUsd) : '-';
        const pnl = t.pnlPct != null ? `${(t.pnlPct > 0 ? '+' : '')}${(t.pnlPct * 100).toFixed(2)}%` : '-';
        const duration = t.entryTime ? formatDuration(t.timestamp - t.entryTime) : '-';
        const alpha = t.alpha || 'MANUAL';
        
        const computedProfitSol = t.profitSol != null ? t.profitSol : ((t.pnlUsd || 0) / solPrice);
        const profitSolStr = `${computedProfitSol >= 0 ? '+' : ''}${computedProfitSol.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SOL`;
        const pnlWithSol = `${pnl} (${profitSolStr})`;
        
        text += `${t.mint} | ${name} | ${entryMc} | ${exitMc} | ${pnlWithSol} | ${duration} | ${alpha}\n`;
      });
    }
    
    if (!text) {
      addAppToast("No data to copy.", "error");
      return;
    }

    navigator.clipboard.writeText(text).then(() => addAppToast("Copied positions and history to clipboard!", "success"));
  };

  const manualRefresh = () => {
    fetch("/api/server-paper-open-positions")
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setOpenPositions(data);
        setNow(Date.now());
        setLastPollTime(Date.now());
      })
      .catch(err => console.error(err));
  };



  const getNormalizedTs = useCallback((t: any) => {
    let v = t.timestamp || t.exitTime || t.entryTime || 0;
    if (typeof v === "string") v = Number(v) || Date.parse(v) || 0;
    if (v > 0 && v < 1e11) v *= 1000;
    return v;
  }, []);

  // When changing timeframe, ensure we also query the server in case more matching trades exist
  useEffect(() => {
    if (timeframe === "All") return;
    fetch(`/api/server-paper-trades?timeframe=${timeframe}&limit=50`)
      .then(res => res.json())
      .then(data => {
        if (data && Array.isArray(data.trades) && data.trades.length > 0) {
          setTrades(prev => {
            const existingIds = new Set(prev.map(t => t.id || `${t.mint}_${t.timestamp}`));
            const incoming = data.trades.filter((t: any) => !existingIds.has(t.id || `${t.mint}_${t.timestamp}`));
            if (incoming.length === 0) return prev;
            return [...prev, ...incoming].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          });
        }
      })
      .catch(() => {});
  }, [timeframe]);

  // — Stats Computation (Backend Accelerated) —
  const allClosedTrades = useMemo(() => trades.filter(t => t.action === "sell"), [trades]);

  const closedTrades = useMemo(() => {
    if (timeframe === "All") return allClosedTrades;
    const config = TIMEFRAME_CONFIG.find(c => c.id === timeframe);
    if (!config || !isFinite(config.ms)) return allClosedTrades;

    const latestTs = allClosedTrades.reduce((max, t) => Math.max(max, getNormalizedTs(t)), 0);
    // If trades are recent (within 1h of now), anchor to now. Otherwise anchor to latest trade timestamp
    const refTime = (now - latestTs < 3600000 && latestTs > 0) ? now : (latestTs || now);
    const cutoff = refTime - config.ms;

    const filtered = allClosedTrades.filter(t => getNormalizedTs(t) >= cutoff);
    // If exact cutoff yields empty (e.g. slight variance), fall back to most recent trades
    if (filtered.length === 0 && allClosedTrades.length > 0) {
      return allClosedTrades.slice(0, 15);
    }
    return filtered;
  }, [allClosedTrades, timeframe, now, getNormalizedTs]);

  // Read backend precalculated scoped metrics if available
  const effectiveServerStats = localServerStats || serverPaperStats;
  const activeScopeStats = 
    effectiveServerStats?.scopes?.[timeframe] || 
    (effectiveServerStats?.activeTimeframe === timeframe ? effectiveServerStats?.selectedScope : null) ||
    (timeframe === "All" ? effectiveServerStats?.scopes?.["All"] : null) ||
    effectiveServerStats?.selectedScope;

  // Client-derived fallbacks in case network is pending
  const clientWinCount = closedTrades.filter(t => (t.profitSol || t.pnlUsd || 0) > 0).length;
  const clientWinRate = closedTrades.length > 0 ? (clientWinCount / closedTrades.length) * 100 : 0;
  const clientTotalPnlSol = closedTrades.reduce((acc, t) => acc + (t.profitSol != null ? t.profitSol : ((t.pnlUsd || 0) / solPrice)), 0);
  const clientTotalEntrySol = closedTrades.reduce((acc, t) => acc + (t.profitSol != null && t.pnlPct ? t.profitSol / t.pnlPct : ((t.amountUsd || 0) / solPrice)), 0);
  const clientTotalPnlPct = clientTotalEntrySol > 0 ? (clientTotalPnlSol / clientTotalEntrySol) * 100 : 0;
  const clientAvgPnlPct = closedTrades.length > 0 ? (closedTrades.reduce((sum, t) => sum + (t.pnlPct || 0), 0) / closedTrades.length) * 100 : 0;
  const clientPnlPctArray = closedTrades.map(t => t.pnlPct ?? 0);
  const clientPnlPctStdDev = clientPnlPctArray.length > 0 ? Math.sqrt(clientPnlPctArray.reduce((sum, val) => sum + Math.pow(val - clientAvgPnlPct / 100, 2), 0) / clientPnlPctArray.length) : 0;
  const clientSharpeRatio = clientPnlPctStdDev !== 0 ? ((clientAvgPnlPct / 100) / clientPnlPctStdDev).toFixed(2) : '0.00';
  const clientLiquidations = closedTrades.filter(t => (t.pnlPct || 0) <= -0.99).length;

  const clientAllEvents: { time: number; type: 'buy' | 'sell'; amount: number }[] = [];
  closedTrades.forEach(t => {
    const entry = t.profitSol != null && t.pnlPct ? t.profitSol / t.pnlPct : ((t.amountUsd || 0) / solPrice);
    const exit = entry + (t.profitSol != null ? t.profitSol : ((t.pnlUsd || 0) / solPrice));
    clientAllEvents.push({ time: t.entryTime || (t.timestamp - 1), type: 'buy', amount: entry });
    clientAllEvents.push({ time: t.timestamp, type: 'sell', amount: exit });
  });
  openPositions.forEach(p => {
    const entry = (p.sizeUsd || 0) / solPrice;
    clientAllEvents.push({ time: p.timestamp, type: 'buy', amount: entry });
  });
  clientAllEvents.sort((a, b) => a.time - b.time);
  let clientSimBalance = 0;
  let clientMinBalance = 0;
  clientAllEvents.forEach(e => {
    if (e.type === 'buy') clientSimBalance -= e.amount;
    else clientSimBalance += e.amount;
    if (clientSimBalance < clientMinBalance) clientMinBalance = clientSimBalance;
  });
  const clientPeakCapitalRequired = Math.abs(clientMinBalance);

  let cumPnl = 0;
  let cumEntry = 0;
  const clientChartData = closedTrades
    .slice()
    .sort((a, b) => a.timestamp - b.timestamp)
    .map(t => {
      const pnl = t.profitSol != null ? t.profitSol : ((t.pnlUsd || 0) / solPrice);
      const entry = t.profitSol != null && t.pnlPct ? t.profitSol / t.pnlPct : ((t.amountUsd || 0) / solPrice);
      cumPnl += pnl;
      cumEntry += entry;
      return {
        time: new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        pnlSol: cumPnl,
        pnlPct: cumEntry > 0 ? (cumPnl / cumEntry) * 100 : 0
      };
    });

  // Pick backend stats first (zero waiting for 1k/3k trades), fallback to client calculations
  const totalPnlSol = activeScopeStats != null ? (activeScopeStats.totalPnlSol ?? 0) : clientTotalPnlSol;
  const totalEntrySol = activeScopeStats != null ? (activeScopeStats.totalEntrySol ?? 0) : clientTotalEntrySol;
  const totalPnlPct = activeScopeStats != null ? (activeScopeStats.totalPnlPct ?? 0) : clientTotalPnlPct;
  const winRate = activeScopeStats != null ? (activeScopeStats.winRate ?? 0) : clientWinRate;
  const avgPnlPct = activeScopeStats != null ? (activeScopeStats.avgPnlPct ?? 0) : clientAvgPnlPct;
  const sharpeRatio = activeScopeStats != null ? (activeScopeStats.sharpeRatio || "0.00") : clientSharpeRatio;
  const liquidations = activeScopeStats != null ? (activeScopeStats.liquidations ?? 0) : clientLiquidations;
  const peakCapitalRequired = activeScopeStats != null ? (activeScopeStats.peakCapitalRequired ?? 0) : clientPeakCapitalRequired;
  const houseMoney = activeScopeStats != null ? (activeScopeStats.houseMoney ?? Math.max(0, totalPnlSol)) : Math.max(0, totalPnlSol);
  const chartData = (activeScopeStats?.chartData && activeScopeStats.chartData.length > 0) ? activeScopeStats.chartData : clientChartData;
  const effectiveClosedCount = activeScopeStats != null ? (activeScopeStats.totalTrades ?? 0) : closedTrades.length;
  const effectiveAllClosedCount = effectiveServerStats?.totalCount ?? allClosedTrades.length;

  const floatingEntrySol = openPositions.reduce((acc, p) => acc + ((p.sizeUsd || 0) / solPrice), 0);
  const floatingPnlSol = openPositions.reduce((acc, p) => acc + (((p.sizeUsd || 0) * (p.pnlPct || 0)) / solPrice), 0);
  const floatingPnlPct = floatingEntrySol > 0 ? (floatingPnlSol / floatingEntrySol) * 100 : 0;

  // Projected if all closed
  const projectedTotalPnlSol = totalPnlSol + floatingPnlSol;
  const projectedTotalEntrySol = totalEntrySol + floatingEntrySol;
  const projectedTotalPnlPct = projectedTotalEntrySol > 0 ? (projectedTotalPnlSol / projectedTotalEntrySol) * 100 : 0;

  const chartColor = totalPnlSol >= 0 ? "#10b981" : "#f43f5e";

  const activeMaxHeight = 800;
  const closedHeight = 800;

  return (
    <div className="mt-4 border-t border-white/5 pt-4 space-y-6 pb-20">



      {/* ── PnL Graph ──────────────────────────────────────────────── */}
      {chartData.length > 0 && (
        <div className="bg-neutral-900/50 border border-white/5 rounded-xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-wider text-white/60">FlyBrain Equity Curve</span>
              <span className="text-[9px] font-mono text-emerald-400/80 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                {timeframe} Scope ({effectiveClosedCount} closed trades)
              </span>
              {activeScopeStats && (
                <span className="flex items-center gap-1 text-[9px] font-mono text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded border border-blue-500/20">
                  <span className="size-1 rounded-full bg-blue-400 animate-pulse" />
                  Server Scoped
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-[10px] font-mono">
              <span className="text-white/40">Scoped PnL:</span>
              <span className={`font-bold ${totalPnlSol >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {totalPnlSol >= 0 ? '+' : ''}{totalPnlSol.toFixed(3)} SOL ({formatPct(totalPnlPct, 2, true)})
              </span>
            </div>
          </div>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorPnl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartColor} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={chartColor} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                <XAxis dataKey="time" stroke="#666" tick={{ fontSize: 10 }} minTickGap={30} />
                <YAxis 
                  yAxisId="right"
                  orientation="right" 
                  stroke="#666" 
                  tick={{ fontSize: 10 }}
                  tickFormatter={(val) => `${val > 0 ? '+' : ''}${val.toFixed(2)} SOL`}
                  domain={['auto', 'auto']} 
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#171717', borderColor: '#333', fontSize: '11px', borderRadius: '8px' }}
                  itemStyle={{ color: chartColor, fontWeight: 'bold' }}
                  labelStyle={{ color: '#888', marginBottom: '4px' }}
                  formatter={(value: any, name: any) => {
                    if (name === 'pnlSol') return [`${value >= 0 ? '+' : ''}${Number(value).toFixed(2)} SOL`, 'House Money'];
                    if (name === 'pnlPct') return [`${value >= 0 ? '+' : ''}${Number(value).toFixed(2)}%`, 'Cumulative ROI'];
                    return [value, name];
                  }}
                />
                <ReferenceLine y={0} yAxisId="right" stroke="#666" strokeDasharray="3 3" />
                <Area yAxisId="right" type="monotone" dataKey="pnlSol" stroke={chartColor} strokeWidth={2} fillOpacity={1} fill="url(#colorPnl)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Trader Calendar & FlyBrain KPIs Launch Button ─────────────── */}
      <button
        type="button"
        onClick={() => setCalendarOpen(true)}
        className="w-full flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-emerald-950/40 via-neutral-900 to-emerald-950/30 border border-emerald-500/30 hover:border-emerald-500/60 transition-all group shadow-[0_0_15px_rgba(16,185,129,0.08)] hover:shadow-[0_0_20px_rgba(16,185,129,0.2)]"
      >
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform shadow-[0_0_10px_rgba(16,185,129,0.2)]">
            <Calendar className="size-4" />
          </div>
          <div className="flex flex-col items-start text-left">
            <span className="text-white text-xs font-bold font-mono group-hover:text-emerald-300 transition-colors flex items-center gap-1.5">
              FlyBrain Trader's Calendar & Journal
              <span className="text-[9px] font-normal px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                {effectiveAllClosedCount} Trades
              </span>
            </span>
            <span className="text-[10px] text-white/40 font-mono">
              Deposits, daily profits & behavioral KPIs over time
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-lg group-hover:bg-emerald-500/20 group-hover:border-emerald-500/40 transition-all">
          <span>View Calendar</span>
          <ArrowUpRight className="size-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </button>
      {/* ── Timeframe Scope Selector Strip ───────────────────────────── */}
      <div className="flex items-center justify-between gap-2 bg-neutral-900/90 border border-white/10 rounded-xl p-1.5 shrink-0 overflow-x-auto custom-scrollbar no-scrollbar">
        <div className="flex items-center gap-1 shrink-0">
          <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-emerald-400/80 pl-1.5 pr-1 font-mono shrink-0">
            <Clock className="size-3 text-emerald-400" />
            <span>Scope:</span>
          </div>
          <div className="flex items-center gap-1 bg-black/50 p-0.5 rounded-lg border border-white/5">
            {TIMEFRAME_CONFIG.map(({ id, label }) => {
              const isSelected = timeframe === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTimeframe(id)}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-mono font-bold transition-all shrink-0 cursor-pointer ${
                    isSelected
                      ? "bg-emerald-500 text-black shadow-[0_0_10px_rgba(16,185,129,0.35)] font-black"
                      : "text-white/50 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="text-[10px] font-mono text-white/40 pr-2 shrink-0 hidden sm:flex items-center gap-1.5">
          <span className="font-bold text-emerald-400">{effectiveClosedCount}</span>
          <span>/</span>
          <span>{effectiveAllClosedCount} closed</span>
          {isStreamingTrades && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[8.5px] animate-pulse">
              <span className="size-1.5 rounded-full bg-emerald-400"></span>
              Streaming {trades.length} of {totalServerTrades || effectiveAllClosedCount}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <PnlCard 
          label={timeframe === "All" ? "Total PnL (Closed)" : `Total PnL (${timeframe})`} 
          pct={totalPnlPct} 
          sol={totalPnlSol} 
          theme="emerald" 
        />

        {/* Floating PnL — with animated delta chip */}
        <PnlCard 
          label="Floating PnL" 
          pct={floatingPnlPct} 
          sol={floatingPnlSol} 
          theme="blue"
        >
          {floatingDelta && floatingEntrySol > 0 && (
            <span
              key={floatingDelta.ts}
              className={`absolute right-2 bottom-2 text-[10px] font-black font-mono pointer-events-none whitespace-nowrap ${floatingDelta.value >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
              style={{ animation: "sbt-float-delta 2.3s ease-out forwards", zIndex: 10 }}
            >
              {floatingDelta.value >= 0 ? '+' : ''}{formatPct((floatingDelta.value / (floatingEntrySol * solPrice)) * 100, 2, false)} {floatingDelta.symbol && `(${floatingDelta.symbol})`}
            </span>
          )}
        </PnlCard>

        {/* Projected if all closed */}
        <PnlCard 
          label="Projected (If Closed)" 
          pct={projectedTotalPnlPct} 
          sol={projectedTotalPnlSol} 
          theme="purple" 
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-neutral-900 border border-white/5 rounded-xl p-3 flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-wider text-white/30 font-bold">Win Rate</span>
          <span className="text-sm font-mono font-bold text-white/80">{formatPct(winRate, 1, false)}</span>
        </div>
        <div className="bg-neutral-900 border border-white/5 rounded-xl p-3 flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-wider text-white/30 font-bold">Closed</span>
          <span className="text-sm font-mono font-bold text-white/80">{effectiveClosedCount}</span>
        </div>
        {/* Avg PnL */}
        <div className="bg-neutral-900 border border-white/5 rounded-xl p-3 flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-wider text-white/30 font-bold">Avg PnL</span>
          <span className={`text-sm font-mono font-bold ${avgPnlPct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{formatPct(avgPnlPct, 2, true)}</span>
        </div>
        {/* Sharpe Ratio */}
        <div className="bg-neutral-900 border border-white/5 rounded-xl p-3 flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-wider text-white/30 font-bold">Sharpe</span>
          <span className="text-sm font-mono font-bold text-white/80">{sharpeRatio}</span>
        </div>
        {/* Capital Deposited */}
        <div className="bg-neutral-900 border border-white/5 rounded-xl p-3 flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-wider text-white/30 font-bold">Deposit</span>
          <span className="text-sm font-mono font-bold text-white/80">{peakCapitalRequired.toFixed(2)} SOL</span>
        </div>
        {/* House Money */}
        <div className="bg-neutral-900 border border-white/5 rounded-xl p-3 flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-wider text-white/30 font-bold">House Money</span>
          <span className="text-sm font-mono font-bold text-emerald-400">{houseMoney.toFixed(2)} SOL</span>
        </div>
        {/* Liquidations */}
        <div className="bg-neutral-900 border border-rose-500/10 rounded-xl p-3 flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-wider text-rose-400/50 font-bold">Liquidated</span>
          <span className="text-sm font-mono font-bold text-rose-400">{liquidations}</span>
        </div>
      </div>

      {/* ── Active Positions ─────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-2">
              <Activity className="size-3 shrink-0" />
              Active Positions ({openPositions.length})
              <span className="relative flex h-2 w-2 ml-1 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            </h4>
            <p className="text-[9px] text-emerald-400/50 mt-1 font-mono max-w-xl">
              Server-side algorithmic trades meant to be executed automatically by the hot wallet on production.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              onClick={handleCloseAll}
              className="flex items-center gap-1.5 px-2 py-1 rounded bg-white/5 border border-white/10 hover:bg-rose-500/20 hover:border-rose-500/40 hover:text-rose-400 text-[9px] text-white/60 transition-colors cursor-pointer whitespace-nowrap"
              title="Close ALL Active Positions"
            >
              <X className="size-2.5 shrink-0" />Close All
            </button>
            <button
              onClick={handleCopyPositions}
              className="flex items-center gap-1.5 px-2 py-1 rounded bg-white/5 border border-white/10 hover:bg-white/10 text-[9px] text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <Copy className="size-2.5 shrink-0" />Copy
            </button>
            <button
              onClick={manualRefresh}
              className="flex items-center gap-1.5 px-2 py-1 rounded bg-white/5 border border-white/10 hover:bg-white/10 text-[9px] text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <RefreshCw className="size-2.5 shrink-0" />Refresh
            </button>
          </div>
        </div>
        <div className="text-[9px] text-white/30 font-mono flex gap-3">
          <span>Last fetched: {formatDuration(now - lastPollTime)} ago</span>
          {lastWsEvent && <span>Last WS event: {formatDuration(now - lastWsEvent)} ago</span>}
        </div>

        <div
          className="bg-neutral-900 border border-emerald-500/20 rounded-xl overflow-hidden"
          style={{ minHeight: 200 }}
        >
          {openPositions.length === 0 ? (
            <div className="text-center text-[11px] text-white/40 p-6 font-mono flex items-center justify-center" style={{ minHeight: 200 }}>
              Waiting for ALgoat signals...
            </div>
          ) : (
            <div className="overflow-y-auto" style={{ maxHeight: Math.max(activeMaxHeight, 200) }}>
              {sortedOpenPositions.map((pos, index) => {
                const meta = pos.tokenMeta || {};
                const anim = animState[pos.mint];
                const pnlPct = pos.pnlPct ?? 0;
                const isProfit = pnlPct > 0;
                const isFreshCatalyst = pos.lastActivity && (now - pos.lastActivity) < 10000;
                const posSizeSol = pos.sizeSol || (pos.totalInvestedUsd ? pos.totalInvestedUsd / solPrice : (pos.sizeUsd ? pos.sizeUsd / solPrice : 0));

                // Which flash animation to play this render cycle
                let rowFlashAnim: string | undefined;
                if (anim) {
                  if (anim.rankMove !== null && anim.rankMove! > 0) {
                    rowFlashAnim = "sbt-row-flash-green 1.4s ease-out forwards";
                  } else if (anim.rankMove !== null && anim.rankMove! < 0) {
                    rowFlashAnim = "sbt-row-flash-red 1.4s ease-out forwards";
                  } else if (anim.pnlDelta !== null && anim.pnlDelta! > 0) {
                    rowFlashAnim = "sbt-row-flash-pnl 0.9s ease-out forwards";
                  }
                }

                // Left border strip color by PnL magnitude
                const borderColor = pnlPct >= 0.1 ? "#10b981"
                  : pnlPct >= 0.02 ? "#34d399"
                  : pnlPct >= 0 ? "#6ee7b7"
                  : pnlPct >= -0.05 ? "#fb7185"
                  : "#f43f5e";

                return (
                  <div
                    key={pos.mint}
                    className="relative border-b border-emerald-500/10 group/row"
                    style={{
                      animation: "sbt-ambient-shimmer 4.5s ease-in-out infinite",
                      animationDelay: `${(index * 650) % 3500}ms`
                    }}
                  >
                    {/* Left PnL color strip */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-[3px] transition-colors duration-700"
                      style={{ background: borderColor }}
                    />

                    {/* Row flash overlay — new key on each anim update restarts the animation */}
                    {anim && rowFlashAnim && (
                      <div
                        key={anim.flashTs}
                        className="absolute inset-0 pointer-events-none"
                        style={{ 
                          animation: rowFlashAnim, 
                          animationDelay: `${anim.delayMs || 0}ms`,
                          zIndex: 0 
                        }}
                      />
                    )}

                    {/* Rank move badge — top-left corner, pops and fades */}
                    {anim?.rankMove !== null && anim?.rankMove !== undefined && anim.rankMove !== 0 && (
                      <div key={`rank-${anim.flashTs}`} className="absolute top-1 left-2 z-30 pointer-events-none">
                        <RankMoveBadge move={anim.rankMove} flashTs={anim.flashTs} delayMs={anim.delayMs} />
                      </div>
                    )}

                    {/* Row content: 2 rows strictly */}
                    <div 
                      onMouseEnter={() => prefetchCandles(pos.mint, "1s")}
                      onTouchStart={() => prefetchCandles(pos.mint, "1s")}
                      className="relative z-10 py-2 px-3 hover:bg-emerald-950/15 transition-colors flex flex-col gap-1.5 text-[10px] font-mono"
                    >
                      
                      {/* ROW 1: Token Identity + Socials (Left) & Live PnL + Close Button (Right) */}
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        {/* Token Identity */}
                        <div className="flex items-center gap-2 min-w-0">
                          <TokenAvatar
                            mint={pos.mint}
                            symbol={meta.symbol}
                            name={meta.name}
                            imageUri={meta.imageUri || meta.image}
                            size="size-7"
                            delayMs={(index * 350) % 2500}
                          />
                          <div className="flex items-center gap-1.5 min-w-0 flex-wrap sm:flex-nowrap">
                            <button
                              onClick={() => setCaMintInput(pos.mint)}
                              onMouseEnter={() => prefetchCandles(pos.mint, "1s")}
                              className="font-black text-xs truncate cursor-pointer hover:underline text-left text-white drop-shadow-sm max-w-[110px] sm:max-w-[150px]"
                              title={meta.name || meta.symbol}
                            >
                              {meta.name || meta.symbol || truncate(pos.mint, 4)}
                            </button>
                            <RunnerScoreBadge score={meta.runnerScore} />
                            <div 
                              className="flex items-center gap-1 cursor-pointer text-white/40 hover:text-white group bg-black/30 px-1 py-0.5 rounded text-[8px] shrink-0" 
                              onClick={() => handleCopy(pos.mint)}
                              title="Copy CA"
                            >
                              <span>{truncate(pos.mint, 4)}</span>
                              <Copy className="size-2 opacity-0 group-hover:opacity-100" />
                            </div>
                            {meta.twitter && (
                              <a href={meta.twitter} target="_blank" rel="noreferrer" className="text-white/30 hover:text-[#1DA1F2] transition-colors shrink-0">
                                <Twitter className="size-2.5" />
                              </a>
                            )}
                            {meta.telegram && (
                              <a href={meta.telegram} target="_blank" rel="noreferrer" className="text-white/30 hover:text-[#0088cc] transition-colors shrink-0">
                                <Send className="size-2.5" />
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Right: PnL Badge, Size & Close Action */}
                        <div className="flex items-center gap-2 shrink-0">
                          {/* Delta ticker */}
                          {anim?.pnlDelta !== null && anim?.pnlDelta !== undefined && (
                            <PnlDeltaTicker key={`delta-${anim.flashTs}`} delta={anim.pnlDelta} delayMs={anim.delayMs} />
                          )}
                          <div className="flex flex-col items-end">
                            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full font-black text-[10px] border ${isProfit ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                              {isProfit ? <TrendingUp className="size-2.5" /> : <TrendingDown className="size-2.5" />}
                              {formatPct(pnlPct * 100, 2, true)}
                            </div>
                            <span className="text-[8px] font-mono text-white/40 mt-0.5 font-bold">
                              {posSizeSol.toFixed(2)} SOL
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              fetch(`/api/bot-close/${pos.mint}`, { method: 'POST' });
                            }}
                            className="p-1 hover:bg-rose-500/20 bg-black/40 border border-white/5 hover:border-rose-500/30 rounded text-white/40 hover:text-rose-400 transition-all shadow-sm"
                            title="Manually Close Trade"
                          >
                            <X className="size-2.5" />
                          </button>
                        </div>
                      </div>

                      {/* ROW 2: Compact Metrics Pill Strip (Single row, clean overflow-x-auto) */}
                      <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar no-scrollbar text-[9px] text-white/70 py-0.5">
                        <div className="flex items-center gap-1 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 text-emerald-300 font-bold shrink-0">
                          <span className="text-emerald-400/60 font-normal">Size:</span>
                          <span>{posSizeSol.toFixed(2)} SOL</span>
                        </div>
                        <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
                          <span className="text-white/40">MC:</span>
                          <span className="text-white/90 font-bold">{formatUsdCompact(pos.entryMcUsd)}</span>
                          <span className="text-white/30">→</span>
                          <span className="text-emerald-300 font-bold">{formatUsdCompact(pos.currentMcUsd)}</span>
                        </div>
                        <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
                          <Clock className="size-2.5 text-blue-400" />
                          <span>{formatDuration(now - pos.timestamp)}</span>
                        </div>
                        <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
                          <Activity className={`size-2.5 ${(now - pos.lastActivity) < 15000 ? 'text-emerald-400 animate-pulse' : 'text-white/40'}`} />
                          <span>{formatDuration(now - pos.lastActivity)}</span>
                        </div>
                        {pos.holderCount ? (
                          <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
                            <span className="text-white/40">H:</span>
                            <span>{pos.holderCount.toLocaleString()}</span>
                          </div>
                        ) : null}
                        {pos.catalystSol !== undefined && pos.catalystSol > 0 && (
                          <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
                            <span className="text-white/40">Cat:</span>
                            <span
                              className={`font-bold ${pos.catalystIsBuy ? 'text-emerald-400' : 'text-rose-400'}`}
                              style={isFreshCatalyst ? { animation: "sbt-catalyst-flash 0.6s ease-in-out 3" } : undefined}
                            >
                              {pos.catalystSol.toFixed(1)} SOL {pos.catalystIsBuy ? 'B' : 'S'}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0 max-w-[130px] truncate">
                          <span className="text-white/40">Rsn:</span>
                          <span className="text-emerald-400 font-bold truncate">{pos.openReason || "MOMENTUM"}</span>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Trade History ─────────────────────────────────────────────── */}
      <div className="space-y-3 pt-4">
        <div>
          <h4 className="text-[10px] font-black uppercase tracking-widest text-white/30 flex items-center gap-2">
            <Target className="size-3" /> Trade History (Closed)
          </h4>
          <p className="text-[9px] text-white/30 mt-1 font-mono">
            Closed positions are placed in a 30-minute penalty box cooldown before they become eligible for re-entry.
          </p>
        </div>

        <div 
          ref={tradesContainerRef}
          onScroll={handleScroll}
          className="bg-neutral-900 border border-white/5 rounded-xl overflow-hidden overflow-y-auto" 
          style={{ maxHeight: `${closedHeight}px` }}
        >
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin size-5 text-white/40" /></div>
          ) : closedTrades.length === 0 ? (
            <div className="text-center text-[11px] text-white/40 py-8 font-mono">No closed trades yet.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {closedTrades.map((t, i) => {
                const meta = t.tokenMeta || {};
                const isProfit = t.pnlPct > 0;
                const duration = t.entryTime ? (t.timestamp - t.entryTime) : 0;
                const cooldownMs = 30 * 60 * 1000;
                const timeSinceExit = now - t.timestamp;
                const inCooldown = timeSinceExit < cooldownMs;
                const cooldownLeft = Math.max(0, cooldownMs - timeSinceExit);
                const entrySol = t.amountSol || (t.profitSol != null && t.pnlPct ? Math.abs(t.profitSol / t.pnlPct) : ((t.amountUsd || 0) / solPrice));

                return (
                  <div 
                    key={i} 
                    onMouseEnter={() => prefetchCandles(t.mint, "1s")}
                    onTouchStart={() => prefetchCandles(t.mint, "1s")}
                    className="py-2 px-3 hover:bg-white/[0.02] transition-colors flex flex-col gap-1.5 text-[10px] font-mono border-t border-white/5 first:border-0"
                  >
                    
                    {/* ROW 1: Token Info & Status (Left) + Realized PnL (Right) */}
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <TokenAvatar
                          mint={t.mint}
                          symbol={meta.symbol}
                          name={meta.name}
                          imageUri={meta.imageUri || meta.image}
                          size="size-7"
                        />
                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap sm:flex-nowrap">
                          <button 
                            onClick={() => setCaMintInput(t.mint)}
                            onMouseEnter={() => prefetchCandles(t.mint, "1s")}
                            className="font-bold text-white text-xs text-left cursor-pointer hover:text-emerald-400 transition-colors truncate max-w-[110px] sm:max-w-[150px]"
                            title={meta.symbol || meta.name}
                          >
                            {meta.symbol || meta.name || truncate(t.mint, 4)}
                          </button>
                          <div 
                            className="flex items-center gap-1 cursor-pointer text-white/40 hover:text-white group bg-black/30 px-1 py-0.5 rounded text-[8px] shrink-0" 
                            onClick={() => handleCopy(t.mint)}
                            title="Copy CA"
                          >
                            <span>{truncate(t.mint, 4)}</span>
                            <Copy className="size-2 opacity-0 group-hover:opacity-100" />
                          </div>
                          {inCooldown ? (
                            <span className="text-[8px] px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400/90 flex items-center gap-1 shrink-0" title={`Penalty Box: ${formatDuration(cooldownLeft)}`}>
                              <Clock className="size-2" />
                              <span>{formatDuration(cooldownLeft)}</span>
                            </span>
                          ) : (
                            <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400/90 flex items-center gap-1 shrink-0" title="Eligible">
                              <Activity className="size-2" />
                              <span>Eligible</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right: Realized PnL & Entry Size */}
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="text-right">
                          <span className={`text-xs font-mono font-black ${isProfit ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {isProfit ? '+' : ''}{(t.profitSol != null ? t.profitSol : ((t.pnlUsd || 0) / solPrice)).toFixed(2)} SOL
                          </span>
                          <span className="text-[8px] font-mono text-white/40 block font-bold">
                            {entrySol.toFixed(2)} SOL size
                          </span>
                        </div>
                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full font-black text-[10px] border ${isProfit ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                          {isProfit ? <TrendingUp className="size-2.5" /> : <TrendingDown className="size-2.5" />}
                          {formatPct(t.pnlPct * 100, 1, true)}
                        </div>
                      </div>
                    </div>

                    {/* ROW 2: Execution Metrics Strip */}
                    <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar no-scrollbar text-[9px] text-white/70 py-0.5">
                      <div className="flex items-center gap-1 bg-white/[0.06] px-1.5 py-0.5 rounded border border-white/10 text-white/90 font-bold shrink-0">
                        <span className="text-white/40 font-normal">Size:</span>
                        <span>{entrySol.toFixed(2)} SOL</span>
                      </div>
                      <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
                        <span className="text-white/40">MC:</span>
                        <span className="text-white/80">{formatUsdCompact(t.entryMcUsd)}</span>
                        <span className="text-white/30">→</span>
                        <span className="text-white/90 font-bold">{formatUsdCompact(t.exitMcUsd)}</span>
                      </div>
                      <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
                        <Clock className="size-2 text-blue-400" />
                        <span>{formatDuration(duration)}</span>
                      </div>
                      <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0 max-w-[120px] truncate">
                        <span className="text-white/40">Open:</span>
                        <span className="text-white/90 truncate">{t.openReason || "MOMENTUM"}</span>
                      </div>
                      <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0 max-w-[120px] truncate">
                        <span className="text-white/40">Close:</span>
                        <span className="text-white/90 truncate">{t.closeReason || t.alpha || "TAKE_PROFIT"}</span>
                      </div>
                      <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
                        <span className="text-white/40">Time:</span>
                        <span>{new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>

                  </div>
                );
              })}

              {isStreamingTrades && (
                <div className="py-2.5 px-3 bg-emerald-500/5 border-t border-emerald-500/10 text-center flex items-center justify-center gap-2 text-[10px] font-mono text-emerald-400">
                  <Loader2 className="animate-spin size-3" />
                  <span>Streaming more trades ({trades.length} of {totalServerTrades || effectiveAllClosedCount})...</span>
                </div>
              )}
              {hasMoreTrades && !isStreamingTrades && (
                <div className="py-2.5 px-3 text-center border-t border-white/5 bg-white/[0.01]">
                  <button
                    type="button"
                    onClick={loadMoreTrades}
                    disabled={isLoadingMore}
                    className="px-3 py-1 text-[10px] font-mono rounded bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 transition-colors cursor-pointer"
                  >
                    {isLoadingMore ? "Loading trades..." : `Load More Trades (${trades.length} of ${totalServerTrades || effectiveAllClosedCount})`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Trader Calendar Modal ────────────────────────────────────── */}
      <TraderCalendarModal
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        trades={trades}
        solPrice={solPrice}
        initialDepositSol={peakCapitalRequired}
        onSelectMint={setCaMintInput}
        backendStats={effectiveServerStats}
      />
    </div>
  );
}
